// The survey's own page (redesign spec §6): the section-page shell, its
// General, Appearance, Steps, Placements, Danger zone and History sections,
// and the live preview following whichever section is being looked at or
// edited.

import {
    Badge,
    buttonVariants,
    Eyebrow,
    RAIL_LINK_CLASS,
    RelatedRail,
    SectionNav,
    type SectionNavItem,
} from "@pej/kore/components";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePlacements } from "@/api/placements";
import { useTemplates } from "@/api/templates";
import type { StepKind, Survey } from "@/api/types";
import { CountNavRow } from "@/components/section-page/count-nav-row";
import { SectionEditProvider, useSectionEditController } from "@/components/section-page/section-edit";
import { SectionPageShell } from "@/components/section-page/section-page-shell";
import { useUnsavedChangesGuard } from "@/components/section-page/use-unsaved-changes-guard";
import type { HistoryScope } from "@/features/audit/audit-filters";
import { HistorySection, useHistoryReveal } from "@/features/audit/entity-history";
import { PlacementsSection, placementNavItems } from "@/features/placements/placements-section";
import type { DraftStep } from "@/features/preview/draft";
import { type PreviewFollow, PreviewPanel } from "@/features/preview/preview-panel";
import { languageLabel } from "./languages";
import { StepsSection, stepNavItems } from "./steps/steps-section";
import { SurveyDangerZone } from "./survey-danger-zone";
import type { SurveyDetailsValues } from "./survey-details-fields";
import { previewTargetFor } from "./survey-preview-target";
import { type StepsFormValues, toDraftSteps } from "./survey-schema";
import { AppearanceSection, GeneralSection, HeaderSection, SECTION, ThankYouSection } from "./survey-sections";

export interface SurveyLive {
    details?: SurveyDetailsValues;
    steps?: StepsFormValues;
}

export function SurveyPage({ survey, workspaceId }: { survey: Survey; workspaceId: string }) {
    return (
        <SectionEditProvider>
            <SurveyPageBody survey={survey} workspaceId={workspaceId} />
        </SectionEditProvider>
    );
}

function SurveyPageBody({ survey, workspaceId }: { survey: Survey; workspaceId: string }) {
    const controller = useSectionEditController();
    const { dialog } = useUnsavedChangesGuard(controller.isActiveSectionDirty);
    const templatesQuery = useTemplates(workspaceId);
    const [live, setLive] = useState<SurveyLive>({});
    const [follow, setFollow] = useState<PreviewFollow | null>({ target: 0, seq: 0 });
    const sortedSteps = useMemo(() => [...survey.steps].sort((a, b) => a.position - b.position), [survey.steps]);
    const [draftStep, setDraftStep] = useState<StepKind | null>(null);
    const [placementDraft, setPlacementDraft] = useState(false);
    const placementsQuery = usePlacements(survey.id);
    const placements = placementsQuery.data;
    // Read through a ref, not a dependency: the preview moves on a nav click
    // or an Edit only. A dependency would re-run the follow effect below
    // whenever the list changes under an active section (a save, a sibling's
    // move) and snap a preview that's playing from start back to its target.
    // The draft "New step" maps to the saved list's length, where the live
    // list carries it.
    const stepIdsRef = useRef<string[]>([]);
    stepIdsRef.current = sortedSteps.map((step) => step.id);

    const followSection = useCallback((sectionId: string) => {
        const target = previewTargetFor(sectionId, stepIdsRef.current);
        if (target !== null) setFollow((previous) => ({ target, seq: (previous?.seq ?? 0) + 1 }));
    }, []);
    const history = useHistoryReveal(SECTION.history);
    const onNavClick = useCallback(
        (sectionId: string) => {
            history.onNavClick(sectionId);
            followSection(sectionId);
        },
        [history.onNavClick, followSection],
    );
    // The survey and its placements in one trail; waits for the placements
    // rather than fetching twice, and falls back to the survey alone if they
    // fail to load.
    const placementsFailed = placementsQuery.isError;
    const historyScope: HistoryScope | null = useMemo(() => {
        if (placements) {
            return {
                targetTypes: ["Survey", "Placement"],
                targetIds: [survey.id, ...placements.map((placement) => placement.id)],
            };
        }
        return placementsFailed ? { targetTypes: ["Survey"], targetIds: [survey.id] } : null;
    }, [survey.id, placements, placementsFailed]);

    const active = controller.activeSection;
    useEffect(() => {
        if (active) followSection(active);
    }, [active, followSection]);

    const onDetailsLive = useCallback(
        (details: SurveyDetailsValues | null) => setLive((prev) => ({ ...prev, details: details ?? undefined })),
        [],
    );
    const onStepsLive = useCallback(
        (steps: StepsFormValues | null) => setLive((prev) => ({ ...prev, steps: steps ?? undefined })),
        [],
    );

    const details = live.details;
    const templates = templatesQuery.data ?? [];
    const templateId = details?.templateId ?? survey.templateId ?? "";
    const steps: DraftStep[] = live.steps
        ? toDraftSteps(live.steps)
        : sortedSteps.map((s) => ({ kind: s.kind, isMetric: s.isMetric, required: s.required, config: s.config }));

    const navItems: SectionNavItem[] = useMemo(
        () => [
            { id: SECTION.general, label: "General", hasError: controller.errorsBySection[SECTION.general] },
            { id: SECTION.appearance, label: "Appearance", hasError: controller.errorsBySection[SECTION.appearance] },
            { id: SECTION.header, label: "Header", hasError: controller.errorsBySection[SECTION.header] },
            ...stepNavItems(survey, draftStep, controller.errorsBySection),
            { id: SECTION.thanks, label: "Thank you", hasError: controller.errorsBySection[SECTION.thanks] },
            ...placementNavItems(placements ?? [], placementDraft, controller.errorsBySection),
            { id: SECTION.danger, label: "Danger zone" },
            { id: SECTION.history, label: "History" },
        ],
        [controller.errorsBySection, survey, draftStep, placements, placementDraft],
    );

    return (
        <SectionPageShell
            backLabel="Surveys"
            backLink={<Link to="/w/$workspaceId/surveys" params={{ workspaceId }} />}
            title={survey.name}
            info={{ id: survey.id, createdAt: survey.createdAt }}
            badges={
                <>
                    <Badge tone={survey.active ? "success" : "neutral"}>{survey.active ? "Active" : "Inactive"}</Badge>
                    {survey.steps.some((step) => step.isMetric) && <Badge tone="accent">Scored</Badge>}
                    <Badge tone="neutral">{languageLabel(survey.language)}</Badge>
                </>
            }
            actions={
                // A link wearing Button's classes, as kore's ErrorPage does: Base
                // UI's Button expects a real <button>, and making it render an
                // <a> either warns or turns the link into role="button".
                <Link
                    to="/w/$workspaceId/surveys/$surveyId/results"
                    params={{ workspaceId, surveyId: survey.id }}
                    className={buttonVariants({ size: "sm" })}
                >
                    View results
                </Link>
            }
            nav={
                <>
                    <SectionNav items={navItems} sticky={false} />
                    <div className="mt-6 border-t border-kore-border pt-4">
                        <Eyebrow size="sm">Activity</Eyebrow>
                        <Link
                            to="/w/$workspaceId/surveys/$surveyId/results"
                            params={{ workspaceId, surveyId: survey.id }}
                            // Raw, never through cn(): see count-nav-row.tsx.
                            className={RAIL_LINK_CLASS}
                        >
                            {/* Label and chevron like the Responses row below, with no value. */}
                            <span className="shrink-0">Results</span>
                            <RelatedRail.RowValue>{null}</RelatedRail.RowValue>
                        </Link>
                        <CountNavRow
                            label="Responses"
                            count={survey.responseCount}
                            render={
                                <Link
                                    to="/w/$workspaceId/surveys/$surveyId/responses"
                                    params={{ workspaceId, surveyId: survey.id }}
                                />
                            }
                        />
                    </div>
                </>
            }
            preview={
                <PreviewPanel
                    surveyName={details?.name ?? survey.name}
                    thankYouText={details ? details.thankYouText.trim() || null : survey.thankYouText}
                    displayTitle={details ? details.displayTitle.trim() || null : survey.displayTitle}
                    displayDescription={details ? details.displayDescription.trim() || null : survey.displayDescription}
                    language={details?.language ?? survey.language}
                    steps={steps}
                    template={templates.find((candidate) => candidate.id === templateId) ?? null}
                    follow={follow}
                />
            }
            onNavClick={onNavClick}
        >
            <GeneralSection survey={survey} onLive={onDetailsLive} />
            <AppearanceSection survey={survey} onLive={onDetailsLive} />
            <HeaderSection survey={survey} onLive={onDetailsLive} />
            <StepsSection survey={survey} onLive={onStepsLive} draft={draftStep} onDraft={setDraftStep} />
            <ThankYouSection survey={survey} onLive={onDetailsLive} />
            <PlacementsSection
                workspaceId={workspaceId}
                surveyId={survey.id}
                draft={placementDraft}
                onDraft={setPlacementDraft}
            />
            <SurveyDangerZone survey={survey} workspaceId={workspaceId} />
            <HistorySection
                workspaceId={workspaceId}
                sectionId={SECTION.history}
                scope={historyScope}
                revealed={history.revealed}
                onReveal={history.reveal}
            />
            {dialog}
        </SectionPageShell>
    );
}
